/* Shopify resolves selected option IDs server-side, including products above 250 variants. */
class LMProduct extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    // Taiga entrance animations create a containing block for fixed descendants.
    // Keep the sticky purchase bar directly under body so it stays at the viewport bottom.
    this.sticky = this.querySelector('[data-lm-sticky-bar]');
    document.body.append(this.sticky);
    this.sticky.addEventListener('click', e => { if (e.target.closest('[data-lm-mobile-atc]')) this.querySelector('[data-lm-atc]').click(); });
    this.addEventListener('change', e => {
      if (e.target.matches('[data-option-value-id]')) this.selectOptions(e.target);
    });
    this.addEventListener('click', e => {
      const thumbnail = e.target.closest('[data-lm-media]');
      if (thumbnail) this.showMedia(thumbnail.dataset.lmMedia);
      if (e.target.closest('[data-lm-plus],[data-lm-minus]')) {
        const input = this.querySelector('[data-lm-qty]');
        const direction = e.target.closest('[data-lm-plus]') ? 1 : -1;
        const step = Number(input.step) || 1;
        input.value = Math.max(Number(input.min) || 1, Math.min(Number(input.max) || Infinity, (Number(input.value) || 1) + direction * step));
      }
      if (e.target.closest('[data-lm-mobile-atc]')) this.querySelector('[data-lm-atc]').click();
    });
    this.querySelector('form')?.addEventListener('submit', e => {
      if (this.loading || this.adding || !this.querySelector('[data-lm-variant-id]').value || this.querySelector('[data-lm-atc]').disabled) { e.preventDefault(); return; }
      if (window.LM?.cart) { e.preventDefault(); this.addToCart(e.currentTarget); }
    });
    this.updateState();
    const sticky = this.sticky;
    const purchaseButton = this.querySelector('[data-lm-atc]');
    const reveal = () => { sticky.hidden = purchaseButton.getBoundingClientRect().bottom >= 0; };
    this.stickyObserver = new IntersectionObserver(reveal); this.stickyObserver.observe(purchaseButton);
    this.scrollListener = reveal; window.addEventListener('scroll', reveal, {passive:true}); reveal();
  }
  disconnectedCallback() { this.controller?.abort(); this.sticky?.remove(); this.stickyObserver?.disconnect(); window.removeEventListener('scroll', this.scrollListener); }
  async addToCart(form) {
    this.adding = true;
    const payload = new FormData(form);
    this.querySelector('[data-lm-atc]').disabled = true; this.sticky.querySelector('[data-lm-mobile-atc]').disabled = true;
    const status = this.querySelector('[data-lm-option-status]'); status.textContent = 'Adding to your bag…';
    try {
      const response = await fetch(window.routes.cart_add_url + '.js', {method:'POST',body:payload,headers:{Accept:'application/json'}});
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Could not add this item. Please try again.');
      status.textContent = 'Added to your bag.';
      document.dispatchEvent(new CustomEvent('cart:updated'));
      await window.LM.cart.open();
    } catch(error) { status.textContent = error.message || 'Could not add this item. Please check your bag before retrying.'; }
    finally { this.adding = false; this.updateState(); }
  }
  state() { return JSON.parse(this.querySelector('[data-lm-variant-state]').textContent); }
  updateState() {
    const sectionStyle = getComputedStyle(this);
    ['--lm-primary-bg','--lm-primary-text','--lm-primary-hover-bg','--lm-primary-hover-text','--lm-yellow','--lm-button-radius'].forEach(key => {
      const value = sectionStyle.getPropertyValue(key); if (value) this.sticky.style.setProperty(key, value);
    });
    const state = this.state();
    state.price = new DOMParser().parseFromString(state.price || '', 'text/html').body.textContent;
    const button = this.querySelector('[data-lm-atc]');
    button.disabled = Boolean(this.adding) || !state.id || !state.available;
    button.textContent = state.id ? (state.available ? this.dataset.addLabel + ' — ' + state.price : 'Sold out') : 'Unavailable combination';
    const mobile = this.sticky.querySelector('[data-lm-mobile-atc]');
    mobile.disabled = button.disabled;
    mobile.textContent = button.disabled ? button.textContent : this.dataset.mobileLabel;
    this.sticky.querySelector('[data-lm-mobile-price]').textContent = state.price || '';
    const media = state.mediaId ? this.querySelector(`[data-media-id="${state.mediaId}"] img`) : null;
    const stickyImage = this.sticky.querySelector('.lm-sticky-image');
    if (media && stickyImage) { stickyImage.removeAttribute('srcset'); stickyImage.src = media.currentSrc || media.src; }
    const qty = this.querySelector('[data-lm-qty]');
    qty.min = state.minimum; qty.step = state.increment;
    if (state.maximum != null) qty.max = state.maximum; else qty.removeAttribute('max');
    const amount = Number(qty.value) || state.minimum;
    qty.value = Math.min(state.maximum ?? Infinity, state.minimum + Math.ceil(Math.max(0, amount - state.minimum) / state.increment) * state.increment);
  }
  showMedia(id) {
    if (!this.querySelector(`[data-media-id="${id}"]`)) return;
    this.querySelectorAll('[data-media-id]').forEach(panel => {
      panel.hidden = panel.dataset.mediaId !== String(id);
      if (panel.hidden) {
        panel.querySelectorAll('video').forEach(video => video.pause());
        panel.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
      }
    });
    this.querySelectorAll('[data-lm-media]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.lmMedia === String(id))));
  }
  async selectOptions(input) {
    this.controller?.abort();
    this.controller = new AbortController();
    const token = this.controller;
    const ids = [...this.querySelectorAll('[data-option-value-id]:checked')].map(el => el.dataset.optionValueId);
    const url = new URL(input.dataset.productUrl || this.dataset.productUrl, location.origin);
    // Combined listing navigation must update all product-specific content and review widgets.
    if (input.dataset.productUrl && url.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) {
      url.searchParams.set('option_values', ids.join(',')); location.assign(url); return;
    }
    url.searchParams.set('option_values', ids.join(','));
    url.searchParams.set('section_id', this.dataset.sectionId);
    // Carry preview_theme_id if Shopify has supplied it in the preview URL.
    const previewId = new URL(location.href).searchParams.get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    const focusId = input.id;
    const error = this.querySelector('[data-lm-option-status]');
    const oldId = this.querySelector('[data-lm-variant-id]');
    oldId.disabled = true;
    this.loading = true;
    this.querySelector('[data-lm-atc]').disabled = true;
    this.sticky.querySelector('[data-lm-mobile-atc]').disabled = true;
    this.querySelector('[data-lm-selection]').setAttribute('aria-busy', 'true');
    error.textContent = 'Updating selection…';
    try {
      const response = await fetch(url, { signal: token.signal });
      if (!response.ok) throw new Error('Selection request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('lm-product');
      if (!next?.querySelector('[data-lm-variant-state]')) throw new Error('Missing product state');
      for (const selector of ['[data-lm-selection]', '[data-lm-price-row]', '[data-lm-dynamic-checkout]']) {
        const old = this.querySelector(selector), replacement = next.querySelector(selector);
        if (old && replacement) old.replaceWith(replacement);
      }
      window.Shopify?.PaymentButton?.init?.();
      const state = this.state();
      this.updateState();
      if (state.mediaId) this.showMedia(state.mediaId);
      const canonical = new URL(location.href);
      canonical.searchParams.delete('option_values'); canonical.searchParams.delete('variant');
      if (state.id) canonical.searchParams.set('variant', state.id); else canonical.searchParams.set('option_values', ids.join(','));
      history.replaceState({}, '', canonical);
      error.textContent = state.id ? (state.available ? 'Selection updated.' : 'This variant is sold out.') : 'This combination is unavailable. Choose another option.';
      this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: state } }));
    } catch (e) {
      if (e.name === 'AbortError') return;
      // Retain the disabled ID: never purchase the previous variant after a failed lookup.
      error.textContent = 'Could not update this selection. Choose an option again to retry.';
    } finally {
      if (this.controller === token) { this.loading = false; this.querySelector('[data-lm-selection]').removeAttribute('aria-busy'); }
    }
  }
}
if (!customElements.get('lm-product')) customElements.define('lm-product', LMProduct);
